/**
 * Auto-Redline Preview Component
 *
 * Displays a single preview for an entire AutoRedlineOutput with all its edits
 * Shows the cumulative effect of all edits in one preview box
 */

import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  AutoRedlineOutput,
  EditStatus,
  EditOperationWithStatus,
  PersistedEditOutcome,
} from "../../interfaces/autoRedlineTypes";
import { useEditApplication } from "./hooks/useEditApplication";
import { applyEditsSequentially } from "./services/wordSequentialEditService";
import { checkParagraphFreshness } from "./services/staleParagraphCheck";
import { useStaleApplyRecorder } from "./hooks/useStaleApplyRecorder";
import { resolveListChange, describeListChange } from "./services/listChangeHelpers";
import { ComposerModeIcon } from "./ComposerModeIcon";
import { NumberingChangeIndicator } from "./NumberingChangeIndicator";
import {
  getCurrentTrackChangesMode,
  getMarkupDisplayMode,
  normalizeTrackChangesMode,
  setTrackChangesMode,
  TrackChangesMode,
} from "../../helpers/word";
import { useTrackChangesRevertNotice } from "../../contexts/TrackChangesRevertNoticeContext";
import { useAgent } from "../../contexts/AgentContext";
import { useSession } from "../../contexts/SessionContext";
import { createLogger } from "../../utils/logger";
import { track } from "../../utils/analytics";
import { sanitizeRedlineHtml } from "../../utils/sanitizeHtml";

const log = createLogger("AutoRedlinePreview");

const STALE_MESSAGE =
  "You've edited this paragraph since the preview was generated, so these " +
  "suggestions no longer match the current text. Ask Contract Space to redo " +
  "the edits.";

/** Word's own wording for the Show Markup states, so the message matches the UI. */
function describeMarkupMode(mode: string): string {
  if (mode === "Balloon") return "revisions in balloons";
  if (mode === "Inline") return "all revisions inline";
  if (mode === "Mixed") return "only comments and formatting in balloons";
  return mode;
}

/**
 * Styling for the info block shown when a preview is blocked from applying
 * because the target paragraph has been edited since the preview was generated.
 * Soft tinted surface with the deeper brand-foreground text colour, kept
 * consistent with the rest of the add-in's informational surfaces.
 */
const STALE_INFO_BLOCK_STYLE: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  alignItems: "flex-start",
  margin: "8px 0 4px",
  padding: "12px 14px",
  backgroundColor: "#F5F9FD",
  border: "1px solid #E2EBF5",
  borderRadius: "10px",
  color: "#0F548C",
  fontSize: "14px",
  lineHeight: "1.5",
};
import {
  getContainerStyle,
  headerStyle,
  diffContainerStyle,
  buttonContainerStyle,
  errorMessageStyle,
  progressIndicatorStyle,
  animationKeyframes,
  editListContainerStyle,
  editListHeaderStyle,
  editListStyle,
  editListItemStyle,
  getEditStatusIconStyle,
  getEditDescriptionStyle,
  editErrorStyle,
  headerCounterStyle,
  successCounterStyle,
  expandButtonStyle,
  getApplyButtonStyle,
  composerModeButtonStyle,
  numberedPreviewRowStyle,
  numberingColumnStyle,
  numberedPreviewTextStyle,
} from "./styles/editPreviewStyles";

function describeTrackChangesMode(mode: TrackChangesMode): string {
  if (mode === "off") return "Off";
  if (mode === "trackAll") return "For Everyone";
  return "Just Mine";
}

/** Handle exposed to parent for programmatic control */
export interface AutoRedlinePreviewHandle {
  applyAll: () => Promise<void>;
  isApplying: () => boolean;
}

interface AutoRedlinePreviewProps {
  /** The complete auto-redline output with all edits */
  redline: AutoRedlineOutput;

  /** Whether composer mode is active (for future auto-apply) */
  composerMode?: boolean;

  /** Whether the component is read-only */
  isReadOnly?: boolean;

  /** Whether streaming is in progress */
  isStreaming?: boolean;

  /**
   * Id of the assistant message this preview belongs to. Used to record
   * stale-apply attempts onto the message so the next request to the BE carries
   * that context. Optional — if absent (e.g. some test scenarios), the stale
   * check still runs and blocks apply, just without the history annotation.
   */
  messageId?: string;
}

/**
 * Component for rendering a single auto-redline preview with all its edits
 */
export const AutoRedlinePreview = forwardRef<AutoRedlinePreviewHandle, AutoRedlinePreviewProps>(
  ({ redline, composerMode = false, isReadOnly = false, isStreaming = false, messageId }, ref) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [applyProgress, setApplyProgress] = useState<
      { current: number; total: number } | undefined
    >();

    // Set when the apply was blocked because the target paragraph changed since
    // the preview was generated. Drives the "stale" visual state and disables
    // the apply button. Local to this preview (each card decides independently).
    const [isStale, setIsStale] = useState(false);

    // Why the apply was blocked, when it is something more specific than "the
    // paragraph changed". Null falls back to STALE_MESSAGE, which is only
    // accurate when the user really did edit the text.
    const [staleReason, setStaleReason] = useState<string | null>(null);

    // Synchronous in-flight lock for the apply pipeline. An apply spans several
    // seconds of Word roundtrips, while the button's disabled state derives from
    // React state that only commits after results return — so without this lock a
    // second click mid-apply starts a concurrent run against the same paragraph,
    // double-applying edits and false-tripping the stale guard (the guard then
    // compares the BE snapshot against text the first run already changed). A ref
    // is deliberate: state updates commit asynchronously and can't close that
    // re-entry window.
    const isApplyInFlightRef = useRef(false);

    // Publishes the Track Changes revert notice to the App-level banner so it
    // doesn't visually conflict with this card's success-state background.
    const { showNotice: showRevertNotice } = useTrackChangesRevertNotice();

    // Used to record stale-apply attempts onto the owning assistant message so
    // the LLM sees the blocked-apply context on the next user turn.
    const { currentAgent } = useAgent();
    const { getAgentSession, updateMessageForAgent } = useSession();

    // Fires the analytics event and annotates the owning message when the guard
    // blocks an apply.
    const recordStaleApplyAttempt = useStaleApplyRecorder(messageId);

    // Track edit statuses locally for this redline only
    const [editStatuses, setEditStatuses] = useState<Map<string, EditOperationWithStatus>>(
      new Map()
    );

    // Initialize edit statuses when redline changes. Persisted terminal outcomes
    // (redline.editStatuses, written after each apply run) win over missing or
    // transient local state — this is what restores applied/failed after the
    // card remounts (tab change / agent switch), and also what flips a remounted
    // card to "applied" when a background apply finishes after the remount (the
    // persist produces a new redline object, re-running this effect).
    useEffect(() => {
      const newStatuses = new Map<string, EditOperationWithStatus>();
      redline.edits.forEach((edit) => {
        const existing = editStatuses.get(edit.editId);
        const persisted = redline.editStatuses?.[edit.editId];
        if (
          persisted &&
          (!existing || existing.status === "pending" || existing.status === "applying")
        ) {
          newStatuses.set(edit.editId, {
            ...edit,
            status: persisted.status,
            errorMessage: persisted.errorMessage,
            appliedAt: persisted.appliedAt,
          });
        } else if (existing) {
          // Preserve existing status (identical to the persisted copy when both
          // are terminal)
          newStatuses.set(edit.editId, existing);
        } else {
          newStatuses.set(edit.editId, {
            ...edit,
            status: "pending",
          });
        }
      });
      setEditStatuses(newStatuses);
    }, [redline]);

    // Persist terminal per-edit outcomes onto the owning AutoRedlineOutput in
    // message.inlineToolEvents (addComment pattern), so the applied/failed state
    // survives remounts and feeds the status-aware conversation history. Reads
    // the LIVE session at write time (like the stale write-back below) so
    // concurrent applies on sibling cards and post-unmount completions land
    // correctly.
    const persistEditOutcomes = useCallback(
      (outcomes: Record<string, PersistedEditOutcome>) => {
        if (!messageId || !currentAgent?.id || Object.keys(outcomes).length === 0) return;
        const agentId = currentAgent.id;
        const session = getAgentSession(agentId);
        const owningMessage = session?.messages.find((m) => m.id === messageId);
        const events = owningMessage?.inlineToolEvents;
        // Legacy message without events — degrade to local-only (current behavior)
        if (!events) return;

        let touched = false;
        const updatedEvents = events.map((evt) => {
          if (evt.type !== "autoRedline" || !evt.data.autoRedlineOutputs) return evt;
          let evtTouched = false;
          const outputs = evt.data.autoRedlineOutputs.map((output) => {
            // tool_use_id is unique per tool call (primary key). Reference
            // equality is the fallback for outputs that never got a tool_use_id
            // — the redline prop IS the session object in that case.
            const isThisOutput = redline.tool_use_id
              ? output.tool_use_id === redline.tool_use_id
              : output === redline;
            if (!isThisOutput) return output;
            evtTouched = true;
            touched = true;
            return { ...output, editStatuses: { ...output.editStatuses, ...outcomes } };
          });
          return evtTouched ? { ...evt, data: { ...evt.data, autoRedlineOutputs: outputs } } : evt;
        });
        if (touched) {
          updateMessageForAgent(agentId, messageId, { inlineToolEvents: updatedEvents });
        }
      },
      [messageId, currentAgent, getAgentSession, updateMessageForAgent, redline]
    );

    // Use the edit application hook locally for this redline
    const { isAnyEditApplying } = useEditApplication({
      autoRedlines: [redline], // Pass only this redline
      onEditStatusChange: (editId, status, error) => {
        setEditStatuses((prev) => {
          const newMap = new Map(prev);
          const edit = newMap.get(editId);
          if (edit) {
            newMap.set(editId, {
              ...edit,
              status,
              errorMessage: error,
              appliedAt: status === "applied" ? Date.now() : undefined,
            });
          }
          return newMap;
        });
      },
    });

    // Inject animation styles into the document
    useEffect(() => {
      const styleId = "autoredline-animations";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.innerHTML = animationKeyframes;
        document.head.appendChild(style);
      }
    }, []);

    // Calculate overall status based on individual edit statuses
    const getOverallStatus = useCallback((): EditStatus => {
      const statuses = redline.edits.map((edit) => {
        const editStatus = editStatuses.get(edit.editId);
        return editStatus?.status || "pending";
      });

      // If any are applying, overall is applying
      if (statuses.includes("applying")) return "applying";

      // If all are applied, overall is applied
      if (statuses.every((s) => s === "applied")) return "applied";

      // If any failed, overall is failed
      if (statuses.includes("failed")) return "failed";

      // Otherwise pending
      return "pending";
    }, [redline.edits, editStatuses]);

    // Count applied and failed edits
    const appliedCount = redline.edits.filter(
      (edit) => editStatuses.get(edit.editId)?.status === "applied"
    ).length;
    const failedCount = redline.edits.filter(
      (edit) => editStatuses.get(edit.editId)?.status === "failed"
    ).length;

    // Get any error messages
    const errorMessages = redline.edits
      .map((edit) => editStatuses.get(edit.editId)?.errorMessage)
      .filter(Boolean);

    const overallStatus = getOverallStatus();
    const hasEdits = redline.edits.length > 0;
    const isApplying = overallStatus === "applying";
    const isComplete = overallStatus === "applied";
    const hasFailed = overallStatus === "failed";

    // Handle apply all button click. `mode` distinguishes the user's manual
    // Apply from the composer-mode auto-apply loop (which enters via the
    // imperative handle below) - analytics-only, no behavior difference.
    const handleApplyAll = useCallback(
      async (mode: "apply_all" | "composer_auto" = "apply_all") => {
        if (isReadOnly || isAnyEditApplying() || isStale) return;
        if (!hasEdits) return;
        if (isApplyInFlightRef.current) {
          log.info("Apply ignored — already in flight", { sectionName: redline.sectionName });
          return;
        }

        // Get pending edit IDs for this redline. Failed edits are deliberately
        // excluded: a failed edit's search coordinates were built by the BE
        // against a snapshot that no longer matches, so re-applying the same edit
        // can only fail again. There is no retry — the user asks Contract Space to
        // redo the edits, which produces fresh coordinates.
        const pendingEditIds = redline.edits
          .filter((edit) => {
            const editStatus = editStatuses.get(edit.editId);
            return !editStatus || editStatus.status === "pending";
          })
          .map((edit) => edit.editId);

        if (pendingEditIds.length === 0) return;

        // No await between the check above and this set, so re-entry is excluded.
        isApplyInFlightRef.current = true;

        track("redline:apply_clicked", {
          mode,
          edit_count: pendingEditIds.length,
        });

        // Set by the stale guard below and reported on apply_completed, so we can
        // tell whether self-attributed applies fail more often than normal ones.
        let selfAttributed = false;

        try {
          // Set progress
          setApplyProgress({ current: 0, total: pendingEditIds.length });

          // Restore Track Changes to the state captured when this preview was generated.
          // The BE generated these edits under a specific TC mode; applying under a
          // different mode (e.g. deletions present vs. removed) can misplace edits or
          // fail searches. If the user toggled TC mid-flight, revert it before applying.
          const captured = normalizeTrackChangesMode(redline.trackChangesMode);
          if (captured !== "unknown") {
            const current = await getCurrentTrackChangesMode();
            if (current !== "unknown" && current !== captured) {
              const setResult = await setTrackChangesMode(captured);
              if (setResult.ok) {
                showRevertNotice(
                  `Track Changes was returned to '${describeTrackChangesMode(captured)}' to keep these edits accurate. You had switched it to '${describeTrackChangesMode(current)}' since the preview was generated.`
                );
              } else if (!setResult.unsupported) {
                // Hard failure setting the mode — don't apply edits under the wrong
                // representation; surface to the user.
                throw new Error(
                  `Could not restore Track Changes to '${describeTrackChangesMode(captured)}' before applying edits: ${setResult.error ?? "unknown error"}`
                );
              }
              // unsupported: skip revert silently, proceed (mirrors enableTrackChanges)
            }
          }

          // Display-mode guard. Runs BEFORE the stale check on purpose: a
          // changed Show Markup setting makes the snapshot mismatch too, so
          // without this the stale check fires first and tells the user they
          // edited a paragraph they never touched.
          //
          // Unlike Track Changes above we do NOT put the setting back. It is a
          // per-user viewing preference rather than a property of the edits, so
          // changing it silently would be a bigger surprise than declining to
          // apply. Both sides must be known for the comparison to mean anything.
          const capturedMarkup = redline.markupMode;
          if (capturedMarkup && capturedMarkup !== "unknown") {
            const liveMarkup = await getMarkupDisplayMode();
            if (liveMarkup !== "unknown" && liveMarkup !== capturedMarkup) {
              log.info("auto_redline blocked: markup display mode changed since generation", {
                sectionName: redline.sectionName,
                capturedMarkup,
                liveMarkup,
              });
              setStaleReason(
                `Word's Show Markup setting changed since this preview was generated ` +
                  `(${describeMarkupMode(capturedMarkup)} to ${describeMarkupMode(liveMarkup)}), ` +
                  `so these suggestions no longer match what Word is showing. Switch it back ` +
                  `under Review > Show Markup, or ask Contract Space to redo the edits.`
              );
              setIsStale(true);
              return; // Block the apply — whole-preview atomic.
            }
          }

          // Stale-paragraph guard (see staleParagraphCheck.ts). Only runs for
          // previews with a paragraphId AND a snapshot to compare against;
          // document-wide redlines have no anchor.
          if (redline.paragraphId !== undefined && redline.searchParagraph) {
            try {
              const verdict = await checkParagraphFreshness(redline, messageId);
              selfAttributed = verdict.selfAttributed;

              if (!verdict.matched) {
                setIsStale(true);
                recordStaleApplyAttempt(redline);
                return; // Block the apply — whole-preview atomic.
              }
            } catch (staleCheckError) {
              // The check itself failed (Word.run threw, etc.). Don't treat that
              // as stale — fall through and let any real failure surface through
              // the existing error flow.
              log.warn("auto_redline stale check threw; proceeding with apply", {
                sectionName: redline.sectionName,
                error: staleCheckError instanceof Error ? staleCheckError.message : staleCheckError,
              });
            }
          }

          // Use sequential function to handle dependencies between edits
          const results = await applyEditsSequentially(redline, pendingEditIds, messageId);

          // Process results and update statuses
          const outcomes: Record<string, PersistedEditOutcome> = {};
          results.forEach((result, index) => {
            outcomes[result.editId] = result.success
              ? { status: "applied", appliedAt: Date.now() }
              : { status: "failed", errorMessage: result.error };
            setEditStatuses((prev) => {
              const newMap = new Map(prev);
              const edit = newMap.get(result.editId);
              if (edit) {
                newMap.set(result.editId, {
                  ...edit,
                  status: result.success ? "applied" : "failed",
                  errorMessage: result.success ? undefined : result.error,
                  appliedAt: result.success ? Date.now() : undefined,
                });
              }
              return newMap;
            });

            // Update progress
            setApplyProgress({ current: index + 1, total: pendingEditIds.length });
          });
          persistEditOutcomes(outcomes);

          track("redline:apply_completed", {
            mode,
            applied_count: results.filter((r) => r.success).length,
            failed_count: results.filter((r) => !r.success).length,
            self_attributed: selfAttributed,
          });
        } catch (error) {
          console.error("Failed to apply edits sequentially", error);
          // Surface the real failure reason — for the Track Changes hard-failure
          // path this carries the descriptive message thrown above, otherwise
          // whatever applyEditsSequentially propagated. Falls back to the generic
          // string for non-Error throws.
          const failureMessage =
            error instanceof Error && error.message ? error.message : "Sequential operation failed";
          const outcomes: Record<string, PersistedEditOutcome> = {};
          pendingEditIds.forEach((editId) => {
            outcomes[editId] = { status: "failed", errorMessage: failureMessage };
            setEditStatuses((prev) => {
              const newMap = new Map(prev);
              const edit = newMap.get(editId);
              if (edit) {
                newMap.set(editId, {
                  ...edit,
                  status: "failed",
                  errorMessage: failureMessage,
                });
              }
              return newMap;
            });
          });
          persistEditOutcomes(outcomes);

          track("redline:apply_completed", {
            mode,
            applied_count: 0,
            failed_count: pendingEditIds.length,
            self_attributed: selfAttributed,
          });
        } finally {
          // Clear progress
          setApplyProgress(undefined);
          isApplyInFlightRef.current = false;
        }
      },
      [
        redline,
        editStatuses,
        isReadOnly,
        isAnyEditApplying,
        hasEdits,
        isStale,
        showRevertNotice,
        messageId,
        currentAgent,
        getAgentSession,
        updateMessageForAgent,
        persistEditOutcomes,
      ]
    );

    // Expose handleApplyAll to parent component for composer mode. Composer mode
    // iterates ref.applyAll() over previews; stale and failed ones must quietly
    // no-op so the loop continues and the card stays in its terminal visual
    // state (retry is not offered — failed edits never re-apply).
    useImperativeHandle(
      ref,
      () => ({
        applyAll: async () => {
          if (!hasEdits || isReadOnly || isComplete || isStale || hasFailed) return;
          await handleApplyAll("composer_auto");
        },
        isApplying: () => isApplying || isAnyEditApplying(),
      }),
      [
        handleApplyAll,
        hasEdits,
        isReadOnly,
        isComplete,
        isStale,
        hasFailed,
        isApplying,
        isAnyEditApplying,
      ]
    );

    // Create safe HTML content for preview. previewText is backend-built HTML that
    // interpolates document text into <s>/<u> markup, so it is sanitized before it
    // reaches dangerouslySetInnerHTML (a hostile .docx could otherwise inject
    // script into the task pane).
    const createPreviewMarkup = () => {
      return { __html: sanitizeRedlineHtml(redline.previewText) };
    };

    // Native-Word-numbering column content (OUTPUT-LEVEL, sibling of
    // previewText). Same inline-HTML redline convention as previewText, so it
    // is rendered through the same dangerouslySetInnerHTML mechanism. null /
    // absent / empty means "no numbering column" — keep the single-column
    // layout with no empty gutter.
    const numberingPreview =
      typeof redline.numberingPreview === "string" && redline.numberingPreview.length > 0
        ? redline.numberingPreview
        : undefined;

    // OUTPUT-LEVEL structured numbering change (sibling of numberingPreview,
    // mutually exclusive with it). When present it renders a Word-Ribbon-style
    // Fluent icon + tooltip in the gutter INSTEAD of the numberingPreview HTML.
    // Render precedence: numberingChange → numberingPreview → single-column.
    const numberingChange =
      redline.numberingChange && typeof redline.numberingChange === "object"
        ? redline.numberingChange
        : undefined;

    // Determine button text with better feedback
    const getButtonText = () => {
      if (isStale) return "Apply Unavailable";
      if (isComplete) return "✓ All Applied Successfully";
      if (isApplying) {
        if (applyProgress) {
          return `⏳ Applying... (${applyProgress.current}/${applyProgress.total})`;
        }
        return `⏳ Applying... (${appliedCount}/${redline.edits.length})`;
      }
      if (hasFailed) {
        return failedCount === redline.edits.length
          ? "✗ Couldn't Apply"
          : `⚠ ${failedCount} Couldn't Apply`;
      }
      if (redline.edits.length === 0) return "Preview Only";
      return `Apply All (${redline.edits.length})`;
    };

    return (
      <div
        style={{
          ...getContainerStyle(overallStatus, isHovered),
          transition: "all 0.3s ease",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="article"
        aria-label={`Auto-redline preview for ${redline.sectionName}`}
      >
        {/* Header with section name and edit count. In the stale state the
            counter is omitted; the info block below the diff carries the
            explanation. */}
        <div style={headerStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span>{redline.sectionName || "Suggested Changes"}</span>
            {hasEdits && !isStale && (
              <span style={headerCounterStyle}>
                {isComplete ? (
                  <span style={successCounterStyle}>✓ All {redline.edits.length} applied</span>
                ) : hasFailed ? (
                  <span>
                    {appliedCount}/{redline.edits.length} applied
                    {failedCount > 0 && (
                      <span style={{ color: "#dc3545" }}> ({failedCount} failed)</span>
                    )}
                  </span>
                ) : (
                  `(${appliedCount}/${redline.edits.length} edits)`
                )}
              </span>
            )}
          </div>
          {/* Optional: Expand/collapse button for edit list */}
          {hasEdits && (
            <button onClick={() => setIsExpanded(!isExpanded)} style={expandButtonStyle}>
              {isExpanded ? "Hide" : "Show"} edit list
            </button>
          )}
        </div>

        {/* Preview HTML with diff visualization. Gutter render precedence:
            (1) numberingChange → a Word-Ribbon-style Fluent icon + tooltip
                (+ echoed prior-state label) in the numbering column;
            (2) numberingPreview → the native-numbering HTML column;
            (3) neither → original single-column layout.
            numberingChange and numberingPreview are mutually exclusive. */}
        {numberingChange ? (
          <div style={diffContainerStyle}>
            <div style={numberedPreviewRowStyle}>
              <div style={numberingColumnStyle}>
                <NumberingChangeIndicator change={numberingChange} />
              </div>
              <div
                style={numberedPreviewTextStyle}
                dangerouslySetInnerHTML={createPreviewMarkup()}
              />
            </div>
          </div>
        ) : numberingPreview ? (
          <div style={diffContainerStyle}>
            <div style={numberedPreviewRowStyle}>
              <div
                style={numberingColumnStyle}
                dangerouslySetInnerHTML={{ __html: sanitizeRedlineHtml(numberingPreview) }}
              />
              <div
                style={numberedPreviewTextStyle}
                dangerouslySetInnerHTML={createPreviewMarkup()}
              />
            </div>
          </div>
        ) : (
          <div style={diffContainerStyle} dangerouslySetInnerHTML={createPreviewMarkup()} />
        )}

        {/* Optional: Expanded edit list */}
        {isExpanded && hasEdits && (
          <div style={editListContainerStyle}>
            <div style={editListHeaderStyle}>Edit Operations:</div>
            <ul style={editListStyle}>
              {redline.edits.map((edit) => {
                const editStatus = editStatuses.get(edit.editId);
                const status = editStatus?.status || "pending";
                const error = editStatus?.errorMessage;

                // Create meaningful display text based on operationType
                const getEditDescription = () => {
                  const truncate = (text: string, maxLength: number = 30) => {
                    if (text.length <= maxLength) return text;
                    return text.substring(0, maxLength - 3) + "...";
                  };

                  const operationType = redline.operationType || "edit";
                  const listChange = resolveListChange(edit);

                  // Handle DELETE operations (operationType === 'delete')
                  if (operationType === "delete") {
                    return `Delete paragraph #${redline.paragraphId}`;
                  }

                  // Handle INSERT operations (operationType === 'insert')
                  if (operationType === "insert") {
                    const structInfo = listChange ? ` (${describeListChange(listChange)})` : "";
                    return `Insert "${truncate(edit.replacementText)}" ${edit.position} paragraph #${redline.paragraphId}${structInfo}`;
                  }

                  // Structure-only EDIT (list change with no text change)
                  if (listChange && !edit.searchPhrase) {
                    return `Paragraph #${redline.paragraphId}: ${describeListChange(listChange)}`;
                  }

                  // Standard EDIT operations, optionally with a trailing list change
                  const structSuffix = listChange ? ` + ${describeListChange(listChange)}` : "";
                  if (edit.position === "replace") {
                    if (edit.targetText) {
                      return `"${truncate(edit.targetText)}" → "${truncate(edit.replacementText)}"${structSuffix}`;
                    }
                    return `Replace with "${truncate(edit.replacementText)}"${structSuffix}`;
                  } else if (edit.position === "before") {
                    return `Insert "${truncate(edit.replacementText)}" before "${truncate(edit.searchPhrase)}"${structSuffix}`;
                  } else if (edit.position === "after") {
                    return `Insert "${truncate(edit.replacementText)}" after "${truncate(edit.searchPhrase)}"${structSuffix}`;
                  }
                  return truncate(edit.replacementText) + structSuffix;
                };

                return (
                  <li key={edit.editId} style={editListItemStyle}>
                    {/* Status icon */}
                    <span style={getEditStatusIconStyle(status)}>
                      {status === "applied"
                        ? "✓"
                        : status === "failed"
                          ? "✗"
                          : status === "applying"
                            ? "⏳"
                            : "○"}
                    </span>

                    {/* Edit description */}
                    <span style={getEditDescriptionStyle(status)}>
                      {getEditDescription()}
                      {status === "failed" && error && (
                        <span style={editErrorStyle}>Error: {error}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Error messages. Retrying re-runs the BE's now-stale search
            coordinates and can only fail again, so we don't offer retry — we
            point the user back to Contract Space for a fresh set of edits. */}
        {hasFailed && (
          <div style={errorMessageStyle}>
            {failedCount === 1 && errorMessages.length >= 1
              ? `${errorMessages[0]} `
              : `${failedCount} ${failedCount === 1 ? "edit" : "edits"} couldn't be applied. `}
            Ask Contract Space to redo the edits.
          </div>
        )}

        {/* Progress indicator for composer mode */}
        {isApplying && applyProgress && (
          <div style={progressIndicatorStyle}>
            Applying edit {applyProgress.current} of {applyProgress.total}...
          </div>
        )}

        {/* Stale-state info block. Sits between the diff and the now-disabled
            Apply button so it lands where the user's eye already is when they
            try to apply. */}
        {isStale && (
          <div style={STALE_INFO_BLOCK_STYLE} role="status">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ flexShrink: 0, marginTop: "1px" }}
              aria-hidden="true"
            >
              <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1" />
              <path d="M8 5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="8" cy="11.5" r="1" fill="currentColor" />
            </svg>
            <span>{staleReason ?? STALE_MESSAGE}</span>
          </div>
        )}

        {/* Apply button - Show differently in composer mode */}
        {hasEdits && (
          <div style={buttonContainerStyle}>
            <button
              onClick={() => handleApplyAll()}
              disabled={
                isReadOnly ||
                isStreaming ||
                isComplete ||
                isApplying ||
                composerMode ||
                isStale ||
                hasFailed
              }
              title={isStale ? (staleReason ?? STALE_MESSAGE) : undefined}
              style={
                composerMode && !isComplete && !hasFailed
                  ? composerModeButtonStyle
                  : getApplyButtonStyle(
                      isComplete,
                      hasFailed,
                      failedCount,
                      redline.edits.length,
                      isApplying,
                      isReadOnly || isStale || hasFailed,
                      isStreaming || composerMode
                    )
              }
              aria-label={
                isStale
                  ? (staleReason ?? STALE_MESSAGE)
                  : composerMode && !isComplete
                    ? "Auto-apply enabled"
                    : getButtonText()
              }
            >
              {composerMode && !isComplete && !hasFailed && !isStale ? (
                <>
                  <ComposerModeIcon size={14} whiteMode={true} />
                  <span>Auto-apply enabled</span>
                </>
              ) : (
                getButtonText()
              )}
            </button>
          </div>
        )}

        {/* No edits message */}
        {!hasEdits && (
          <div
            style={{
              marginTop: "12px",
              padding: "8px",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              fontSize: "13px",
              color: "#666",
              textAlign: "center",
            }}
          >
            Preview only - no edits to apply
          </div>
        )}
      </div>
    );
  }
);

AutoRedlinePreview.displayName = "AutoRedlinePreview";
